import { useState, useEffect, useCallback } from "react";
import { getCranes } from "../../repository/BilletYardInventoryRepository";
import { Crane, CranesApiResponse } from "../../models/authModels";

// Raw crane data from API
interface RawCraneData {
  Value: number;
  Title: string;
}

interface UseCranesResult {
  cranes: Crane[];
  selectedCraneId: number | null;
  selectedCrane: Crane | null;
  isLoading: boolean;
  error: string | null;
  loadCranes: () => Promise<void>;
  selectCrane: (craneId: number) => void;
  clearSelection: () => void;
}

/**
 * Hook for managing crane data and selection
 * Loads available cranes from the database and manages crane selection state
 * Replaces hardcoded DEFAULT_CRANE_ID usage
 */
export const useCranes = (autoLoadOnMount: boolean = true): UseCranesResult => {
  const [cranes, setCranes] = useState<Crane[]>([]);
  const [selectedCraneId, setSelectedCraneId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCranes = useCallback(
    async (isMounted?: () => boolean) => {
      if (isMounted && !isMounted()) return;
      setIsLoading(true);
      setError(null);

      try {
        const response: CranesApiResponse = await getCranes();

        if (isMounted && !isMounted()) return;

        if (!response.ok) {
          throw new Error(
            `Failed to load cranes: ${response.message || "Unknown error"}`,
          );
        }

        // Extract crane data from response
        const cranesData =
          (response.data?.tables?.[0]?.rows as unknown as RawCraneData[]) || [];

        // Transform API response to Crane interface
        // API returns: { Title: string, Value: number, Selected: boolean }
        // We need: { CraneID: number, CraneName: string, IsActive: boolean }
        // Filter out "Unknown" option (Value=0), Supervisor (-1), and Torches (-2) - users must explicitly select a real crane
        const transformedCranes: Crane[] = cranesData
          .filter(
            (crane: RawCraneData) =>
              crane.Value !== 0 && crane.Value !== -1 && crane.Value !== -2,
          ) // Remove "Unknown", Supervisor, and Torches cranes
          .map((crane: RawCraneData) => ({
            CraneID: crane.Value,
            CraneName: crane.Title,
            IsActive: true, // All cranes from GetCranesFDDP are active
            Description: undefined,
          }));

        if (isMounted && !isMounted()) return;
        setCranes(transformedCranes);

        // No default crane selection - users must explicitly choose a crane
        // selectedCraneId remains null until user selects one
      } catch (err) {
        if (isMounted && !isMounted()) return;
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load crane data";
        setError(errorMessage);
      } finally {
        if (isMounted && !isMounted()) return;
        setIsLoading(false);
      }
    },
    [selectedCraneId],
  );

  const selectCrane = useCallback((craneId: number) => {
    setSelectedCraneId(craneId);
    setError(null); // Clear any previous errors when selection changes
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCraneId(null);
  }, []);

  // Load cranes on mount if autoLoadOnMount is true
  useEffect(() => {
    let isMounted = true;

    if (autoLoadOnMount) {
      loadCranes(() => isMounted);
    }

    return () => {
      isMounted = false;
    };
  }, [loadCranes, autoLoadOnMount]);

  // Get selected crane object
  const selectedCrane = selectedCraneId
    ? cranes.find((crane) => crane.CraneID === selectedCraneId) || null
    : null;

  return {
    cranes,
    selectedCraneId,
    selectedCrane,
    isLoading,
    error,
    loadCranes: () => loadCranes(),
    selectCrane,
    clearSelection,
  };
};
